import { Button, Checkbox, CircularProgress, DialogActions, FormControlLabel, MenuItem, TextField } from "@mui/material";
import Dialog from "@mui/material/Dialog";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid2";
import {
    ArcElement,
    Chart as ChartJS,
    ChartType,
    DefaultDataPoint,
    Legend,
    Tooltip,
} from "chart.js";
import { useContext, useEffect, useRef, useState } from "react";
import { Pie } from "react-chartjs-2";
import { APIContext } from "../../Contexts/APIContext";
import { SessionContext } from "../../Contexts/SessionContext";
import ExportChartDialog from "../../Site/ExportChartDialog";
import ExportChartViewModel from "../../Site/ViewModels/ExportChartViewModel";
import TripRateCalculationViewModel from "../../TripRate/ViewModels/TripRateCalculationViewModel";
import TripRateDay from "../../TripRate/ViewModels/TripRateDay";
import Directions from "../Directions";
import { getPieBackgroundColours, getPieBorderColours } from "../../Utilities/PieChartPalette";

ChartJS.register(ArcElement, Tooltip, Legend);

interface PieSegment {
    label: string;
    percentage: number;
}

interface IProps {
    onClose: () => void;
    open: boolean;
    tripRate: TripRateCalculationViewModel;
    surveyRefs: string[];
    headerIndex: number;
    hourly: boolean;
}

type ChartOrUndef<TType extends ChartType = ChartType, TData = DefaultDataPoint<TType>, TLabel = unknown> = ChartJS<TType, TData, TLabel> | undefined;

export default function ModalPieChart({ open, onClose, tripRate, surveyRefs, headerIndex, hourly }: IProps) {
    const [direction, setDirection] = useState<string>("Total");
    const [startTime, setStartTime] = useState<string>("");
    const [endTime, setEndTime] = useState<string>("");
    const [useCustomTimeRange, setUseCustomTimeRange] = useState<boolean>(false);
    const [exportDialogOpen, setExportDialogOpen] = useState(false);

    const { sessionState } = useContext(SessionContext);
    const { post, loading, postPDF } = useContext(APIContext);
    const chartRef = useRef<ChartOrUndef<"pie", number[], string>>(undefined);

    const [data, setData] = useState<PieSegment[]>([]);

    const vmKeyFromDirection = (d: string): keyof TripRateCalculationViewModel => {
        const s = d.toLowerCase();
        if (s.startsWith("arriv")) return "arrivals";
        if (s.startsWith("depart")) return "departures";
        return "totals";
    };

    const exportDirectionFromDirection = (d: string): "arrivals" | "departures" | "totals" => {
        const s = d.toLowerCase();
        if (s.startsWith("arriv")) return "arrivals";
        if (s.startsWith("depart")) return "departures";
        return "totals";
    };

    const convertTimeToSegment = (timeString: string | undefined): number | undefined => {
        if (!timeString) return undefined;
        const [hoursStr, minutesStr] = timeString.split(':');
        const hours = parseInt(hoursStr, 10);
        const minutes = parseInt(minutesStr, 10);

        if (isNaN(hours) || isNaN(minutes)) return undefined;

        return (hours * 2) + (minutes === 30 ? 1 : 0);
    };

    const convertSegmentToTime = (segment: string): number => Number(segment) / 2;

    const getFilteredPeriods = () => {
        const key = vmKeyFromDirection(direction);
        const list = (tripRate[key] ?? []) as TripRateDay[];

        return list
            .filter((time) => time.numberOfDays != null && time.numberOfDays > 0)
            .map((time) => {
                const startHourMinute = time.period?.split("-")[0];
                const segmentValue = convertTimeToSegment(startHourMinute);

                if (segmentValue !== undefined) {
                    return (
                        <MenuItem key={segmentValue} value={segmentValue.toString()}>
                            {startHourMinute}
                        </MenuItem>
                    );
                }
                return null;
            })
            .filter((item): item is JSX.Element => item !== null);
    };

    const getData = () => {
        const vm = {
            // Use the same parameter-filtered surveys as the trip-rate calculation.
            // Rebuilding this from session selection can reintroduce sites that were
            // excluded because the selected trip-rate parameter has no value.
            sites: surveyRefs,
            // Include both legacy combined public transport and newer split modes.
            // The server selects one representation so passengers are not double-counted.
            types: [3, 4, 5, 6, 8, 9, 10, 38],
        };

        let queryString = `headerIndex=${headerIndex}&direction=${direction}&hourly=${hourly}`;

        if (useCustomTimeRange) {
            if (startTime) {
                queryString += `&start=${startTime}`;
            }
            if (endTime) {
                queryString += `&end=${Number(endTime)}`;
            }
        }

        post("Chart", `GetModalPieChart?${queryString}`, vm, setData);
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        customCanvasBackgroundColor: { color: "#ffffff" },
        backgroundColor: "#ffffff",
        plugins: {
            backgroundColor: "#ffffff",
            legend: { display: true },
            title: {
                display: true,
                text: `Modal Split Percentages For: ${sessionState.tripRateMain}/${sessionState.tripRateSub}`,
                font: { size: 14, weight: "bold" as const },
                padding: 20,
            },
            tooltip: {
                displayColors: false,
                callbacks: {
                    title: () => "",
                    label: (context: any) => {
                        const item = data[context.dataIndex];
                        return [`${item.label}`, `Percentage: ${item.percentage.toFixed(1) || 0}%`];
                    },
                },
            },
        },
    };

    useEffect(() => {
        getData();
    }, [direction, surveyRefs]);

    useEffect(() => {
        if (!useCustomTimeRange) {
            setStartTime("");
            setEndTime("");
        }
    }, [useCustomTimeRange]);

    const chartData = {
        labels: data.map((m) => m.label),
        datasets: [
            {
                label: "Percentage",
                data: data.map((d) => d.percentage),
                backgroundColor: getPieBackgroundColours(data),
                borderColor: getPieBorderColours(data),
                borderWidth: 1,
            },
        ],
    };

    const exportGraph = () => setExportDialogOpen(true);

    const getReport = (exportFilters: ExportChartViewModel) => {
        exportFilters.data = data;
        exportFilters.surveyDate = null;
        exportFilters.surveyRef = null;
        exportFilters.chartTitle = "Modal Split Percentages";
        exportFilters.options = {
            direction: exportDirectionFromDirection(direction),
            period: useCustomTimeRange && startTime && endTime ? "select" : "all",
            selectedTime: useCustomTimeRange && startTime && endTime
                ? { min: convertSegmentToTime(startTime), max: convertSegmentToTime(endTime) }
                : null,
        };
        exportFilters.type === "PDF" ? getChartPDF(exportFilters) : getChartJPEG();
    };

    const getChartPDF = (exportFilters: ExportChartViewModel) => {
        postPDF("PDFGenerator", "GetModalSplitPiePDF", exportFilters.fileName, false, exportFilters);
    };

    const getChartJPEG = () => {
        downloadJPEG();
    };

    const downloadJPEG = () => {
        const chart = chartRef.current;
        if (!chart) return;

        const src = chart.canvas as HTMLCanvasElement;
        const padding = 16;

        const out = document.createElement("canvas");
        out.width = src.width;
        out.height = src.height + padding;
        const octx = out.getContext("2d");
        if (!octx) return;

        octx.fillStyle = "#ffffff";
        octx.fillRect(0, 0, out.width, out.height);

        octx.drawImage(src, 0, 0);

        const dataUrl = out.toDataURL("image/jpeg", 0.95);

        const a = document.createElement("a");
        a.href = dataUrl;
        const d = new Date();
        const stamp = `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}_${d.getHours()}-${d.getMinutes()}-${d.getSeconds()}`;
        a.download = `modal-split-TripRate-${stamp}.jpg`;
        a.click();
    };

    return (
        <>
            <Dialog open={open} fullWidth maxWidth="md" onClose={onClose}>
                <div
                    style={{
                        padding: "20px 20px 0",
                    }}
                >
                    <Box
                        sx={{
                            width: "100%",
                            maxWidth: 640,
                            height: 640,
                            mx: "auto",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        {loading ? <CircularProgress size={80} /> : <Pie ref={chartRef} data={chartData} options={options} />}
                    </Box>
                </div>

                <Grid container spacing={2} sx={{ p: 2 }}>
                    <Grid size={12}>
                        <Directions setType={setDirection} type={direction} />
                    </Grid>
                    <Grid size={12}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={useCustomTimeRange}
                                    onChange={(e) => setUseCustomTimeRange(e.target.checked)}
                                />
                            }
                            label="Use Custom Time Range"
                        />
                    </Grid>
                    {useCustomTimeRange && (
                        <>
                            <Grid size={12}>
                                <TextField
                                    select
                                    label="Start Time"
                                    value={startTime}
                                    onChange={(e) => setStartTime(e.target.value)}
                                    fullWidth
                                    margin="dense"
                                    disabled={!useCustomTimeRange}
                                    SelectProps={{
                                        MenuProps: {
                                            PaperProps: { style: { maxHeight: 300 } },
                                        },
                                    }}
                                >
                                    {getFilteredPeriods()}
                                </TextField>
                            </Grid>
                            <Grid size={12}>
                                <TextField
                                    select
                                    label="End Time"
                                    value={endTime}
                                    onChange={(e) => setEndTime(e.target.value)}
                                    fullWidth
                                    margin="dense"
                                    disabled={!useCustomTimeRange}
                                    SelectProps={{
                                        MenuProps: {
                                            PaperProps: { style: { maxHeight: 300 } },
                                        },
                                    }}
                                >
                                    {getFilteredPeriods()}
                                </TextField>
                            </Grid>
                            <Button onClick={getData}>Redisplay</Button>
                        </>
                    )}
                </Grid>
                <DialogActions>
                    <Button onClick={() => onClose()} fullWidth={false}>Close</Button>
                    <Button onClick={exportGraph} fullWidth={false}>Export</Button>
                </DialogActions>
            </Dialog>
            <ExportChartDialog dialogOpen={exportDialogOpen} handleClose={() => setExportDialogOpen(false)} exportData={getReport} />
        </>
    );
}
